import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

const DISMISS_KEY = "fka_pwa_dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari never fires beforeinstallprompt → show manual hint.
    if (isIOS()) {
      const t = setTimeout(() => { setIosHint(true); setVisible(true); }, 1500);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onBeforeInstall); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-[60] animate-fade-up">
      <div className="rounded-2xl border border-primary/40 bg-card shadow-2xl p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
          <Download className="text-primary" size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold uppercase tracking-tight text-sm">Installer l'application</p>
          {iosHint ? (
            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
              Appuyez sur <Share size={13} className="inline text-primary" /> puis « Sur l'écran d'accueil » pour ajouter FKA Quaregnon.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">Ajoutez FKA Quaregnon à votre écran d'accueil pour un accès rapide, comme une vraie appli.</p>
          )}
          {!iosHint && (
            <button data-testid="pwa-install-btn" onClick={install}
              className="mt-3 h-9 px-4 rounded-lg bg-primary text-primary-foreground text-xs font-bold">
              Ajouter à l'écran d'accueil
            </button>
          )}
        </div>
        <button data-testid="pwa-dismiss-btn" onClick={dismiss} className="text-muted-foreground hover:text-foreground shrink-0 p-1">
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
